import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { getContentById, type ContentItem } from '../services/contentService';
import { getUserProfile } from '../services/userService';
import { getSubscriptionCount } from '../services/subscriptionsService';

interface VideoPlayerState {
  content: ContentItem | null;
  videoSource: string | null;
  title: string;
  subtitles: string[];
  creatorId: string;
  contentId: string | null;
  uploaderName: string;
  creatorAvatar: string;
  subscriberCount: number;
  isLoading: boolean;
  error: string | null;
}

type VideoPlayerAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_CONTENT'; payload: ContentItem }
  | { type: 'SET_VIDEO_DATA'; payload: Partial<VideoPlayerState> }
  | { type: 'CLEAR_STATE' };

const initialState: VideoPlayerState = {
  content: null,
  videoSource: null,
  title: 'Video Player',
  subtitles: [],
  creatorId: '',
  contentId: null,
  uploaderName: 'Creator',
  creatorAvatar: '',
  subscriberCount: 0,
  isLoading: false,
  error: null,
};

const videoPlayerReducer = (state: VideoPlayerState, action: VideoPlayerAction): VideoPlayerState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload, error: null };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    case 'SET_CONTENT':
      return {
        ...state,
        content: action.payload,
        contentId: action.payload.id,
        title: action.payload.title,
        uploaderName: action.payload.uploader || 'Creator',
        creatorId: action.payload.uploaderId || '',
        creatorAvatar: (action.payload as any).creatorAvatar || '',
        subscriberCount: (action.payload as any).subscriberCount || 0,
        error: null,
        isLoading: false,
      };
    case 'SET_VIDEO_DATA':
      return { ...state, ...action.payload };
    case 'CLEAR_STATE':
      return initialState;
    default:
      return state;
  }
};

interface VideoPlayerContextType {
  state: VideoPlayerState;
  loadContentById: (id: string) => Promise<void>;
  loadContentFromState: (data: Partial<VideoPlayerState>) => void;
  clearState: () => void;
  getVideoSource: () => string | null;
}

const VideoPlayerContext = createContext<VideoPlayerContextType | undefined>(undefined);

export const useVideoPlayer = () => {
  const context = useContext(VideoPlayerContext);
  if (!context) {
    throw new Error('useVideoPlayer must be used within VideoPlayerProvider');
  }
  return context;
};

interface VideoPlayerProviderProps {
  children: React.ReactNode;
}

export const VideoPlayerProvider: React.FC<VideoPlayerProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(videoPlayerReducer, initialState);

  const findVideoInAssets = useCallback((assets: Record<string, any>): string | null => {
    if (!assets) return null;

    const videoKeys = Object.keys(assets).filter(key => {
      const value = assets[key];
      if (typeof value === 'string') {
        return value.includes('.mp4') || value.includes('.mov') || value.includes('.avi') || 
               value.includes('.webm') || value.includes('.mkv') || 
               value.includes('video') || value.includes('firebasestorage') ||
               key.includes('.mp4') || key.includes('.mov') || key.includes('.avi') ||
               key.includes('.webm') || key.includes('.mkv');
      }
      return false;
    });

    if (videoKeys.length > 0) {
      const videoUrl = assets[videoKeys[0]];
      return typeof videoUrl === 'string' ? videoUrl : null;
    }

    return null;
  }, []);

  const loadContentById = useCallback(async (id: string) => {
    if (!id) {
      dispatch({ type: 'SET_ERROR', payload: 'No content ID provided' });
      return;
    }

    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      const contentData = await getContentById(id);
      
      if (!contentData) {
        dispatch({ type: 'SET_ERROR', payload: 'Content not found' });
        return;
      }

      // Fetch creator profile and subscriber count
      let creatorAvatar = '';
      let subscriberCount = 0;
      
      if (contentData.uploaderId) {
        try {
          const [profile, subCount] = await Promise.all([
            getUserProfile(contentData.uploaderId),
            getSubscriptionCount(contentData.uploaderId)
          ]);
          
          if (profile) {
            creatorAvatar = profile.avatarUrl || '';
          }
          subscriberCount = subCount;
        } catch (error) {
          // Silently fail - creator data is non-critical
        }
      }

      const contentWithCreatorData = {
        ...contentData,
        creatorAvatar,
        subscriberCount
      };

      dispatch({ type: 'SET_CONTENT', payload: contentWithCreatorData });
    } catch (error) {
      console.error('Error fetching content:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load content' });
    }
  }, []);

  const loadContentFromState = useCallback((data: Partial<VideoPlayerState>) => {
    dispatch({ type: 'SET_VIDEO_DATA', payload: data });
  }, []);

  const clearState = useCallback(() => {
    dispatch({ type: 'CLEAR_STATE' });
  }, []);

  const getVideoSource = useCallback((): string | null => {
    if (state.videoSource) return state.videoSource;
    if (state.content?.assets) return findVideoInAssets(state.content.assets);
    if (state.content?.trailer) return state.content.trailer;
    return null;
  }, [state.videoSource, state.content, findVideoInAssets]);

  const contextValue: VideoPlayerContextType = {
    state,
    loadContentById,
    loadContentFromState,
    clearState,
    getVideoSource,
  };

  return (
    <VideoPlayerContext.Provider value={contextValue}>
      {children}
    </VideoPlayerContext.Provider>
  );
};
